import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { GlobeIcon, Hotel } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { PilgrimGuideLink } from '../../types/pilgrimGuide';
import { useAccommodationStore } from '../../stores/accommodationStore';

type Props = {
  links: PilgrimGuideLink[];
  packagesMap: Record<string, any>;
}

export const CountryAccommodationChart = ({ links, packagesMap }: Props) => {
  const { translations } = useLanguage();
  const { tables } = useAccommodationStore();
  const [selectedCountry, setSelectedCountry] = useState<string>('all'); // Default to 'all'
  const [countries, setCountries] = useState<string[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const userRole = localStorage.getItem('userRole');

  // Extract unique countries from links
  useEffect(() => {
    if (links.length > 0) {
      const uniqueCountries = Array.from(new Set(
        links.map(link => link.residenceCountry)
      )).sort();
      setCountries(uniqueCountries);
    }
  }, [links]);

  // Create a room number lookup function for checking accommodation status
  const roomNumberLookup = useMemo(() => {
    // Create a mapping of pilgrimId-hotelName to room info for quick lookup
    const lookup: Record<string, { roomNumber: number, roomType: string }> = {};
    
    for (const table of Object.values(tables)) {
      const hotelName = table.hn;
      if (!hotelName) continue;
      
      for (const room of table.r) {
        for (const bed of room.b) {
          if (bed.a) {
            for (const assignment of bed.a) {
              const key = `${assignment.pid}-${hotelName}`;
              lookup[key] = {
                roomNumber: room.n,
                roomType: room.t
              };
            }
          }
        }
      }
    }
    
    return lookup;
  }, [tables]);

  // Function to check if pilgrim has a room assigned in a specific hotel
  const getPilgrimRoomNumber = (pilgrimId: string, hotelName: string) => {
    if (!hotelName) return null;
    const key = `${pilgrimId}-${hotelName}`;
    return roomNumberLookup[key] || null;
  };

  // Calculate accommodation statistics for the selected country by organizer
  const calculateStats = useMemo(() => {
  if (!selectedCountry) return [];

  // Filter links based on selected country (or all countries)
  const countryLinks = selectedCountry === 'all'
    ? links
    : links.filter(link => link.residenceCountry === selectedCountry);

  const isAdminAndAllCountries = userRole === 'admin' && selectedCountry === 'all';

  // Group by either organizer or country
  const groupKey = (link: PilgrimGuideLink) => 
    isAdminAndAllCountries ? link.residenceCountry : link.organizerName;

  const groupedStats = countryLinks.reduce((acc, link) => {
    const key = groupKey(link);

    if (!acc[key]) {
      acc[key] = {
        name: key,
        completed: 0,
        partial: 0,
        pending: 0,
        total: 0
      };
    }

    const pkg = packagesMap[link.packageName];
    if (!pkg) {
      acc[key].pending++;
      acc[key].total++;
      return acc;
    }

    let totalHotels = 1;
    if (pkg.shiftingMakkahHotel) totalHotels++;
    if (pkg.madinahHotel) totalHotels++;

    let accommodatedCount = 0;

    if (getPilgrimRoomNumber(link.pilgrimId, pkg.mainMakkahHotel)) accommodatedCount++;
    if (pkg.shiftingMakkahHotel && getPilgrimRoomNumber(link.pilgrimId, pkg.shiftingMakkahHotel)) accommodatedCount++;
    if (pkg.madinahHotel && getPilgrimRoomNumber(link.pilgrimId, pkg.madinahHotel)) accommodatedCount++;

    if (accommodatedCount === 0) {
      acc[key].pending++;
    } else if (accommodatedCount === totalHotels) {
      acc[key].completed++;
    } else {
      acc[key].partial++;
    }

    acc[key].total++;
    return acc;
  }, {} as Record<string, { name: string; completed: number; partial: number; pending: number; total: number }>);

  return Object.values(groupedStats).sort((a, b) => a.name.localeCompare(b.name));
}, [selectedCountry, links, packagesMap, getPilgrimRoomNumber, userRole]);


  // Update chart data when calculations change
  useEffect(() => {
    setChartData(calculateStats);
  }, [calculateStats]);

  // Custom tooltip to show values and percentages
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, entry: any) => sum + entry.value, 0);
      
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={`item-${index}`} className="flex justify-between items-center mb-1">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm">{entry.name}:</span>
              </div>
              <span className="font-medium ml-4">
                {entry.value} ({total > 0 ? Math.round((entry.value / total) * 100) : 0}%)
              </span>
            </div>
          ))}
          <div className="mt-1 pt-1 border-t text-sm font-medium">
            Total: {total}
          </div>
        </div>
      );
    }
    return null;
  };

  // Get the total for each status across all organizers
  const totals = useMemo(() => {
    if (chartData.length === 0) return { completed: 0, partial: 0, pending: 0, total: 0 };
    
    return chartData.reduce((acc, item) => ({
      completed: acc.completed + item.completed,
      partial: acc.partial + item.partial,
      pending: acc.pending + item.pending,
      total: acc.total + item.total
    }), { completed: 0, partial: 0, pending: 0, total: 0 });
  }, [chartData]);

  // Whether to show the "All Countries" option
  const showAllOption = userRole === 'admin' || userRole === 'organizer';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-xl shadow-sm"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Hotel className="text-primary-500" size={24} />
          </div>
          <h2 className="text-xl font-semibold">
            {translations?.accommodationTasks?.countryChart?.title || 'Accommodation by Country'}
          </h2>
        </div>
        
        {/* Country Selector */}
        <div className="relative inline-block">
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="appearance-none pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {/* Add "All Countries" option for admin and organizer */}
            {showAllOption && (
              <option value="all">
                {translations?.accommodationTasks?.countryChart?.allCountries || 'All Countries'}
              </option>
            )}
            {countries.map(country => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center px-3">
            <GlobeIcon className="text-gray-400" size={18} />
          </div>
        </div>
      </div>

      {/* Display message when no data */}
      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-500">
          {translations?.accommodationTasks?.countryChart?.noData || 'No data available for this country'}
        </div>
      ) : (
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
              maxBarSize={65} // Set maximum width for bars
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={0}
                textAnchor="end"
                height={100}
                tick={{ fontSize: 15 }}
                interval={0}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="completed" 
                stackId="a" 
                fill="#22C55E" 
                name={translations?.accommodationTasks?.statuses?.completed || "Completed"}
              >
                <LabelList dataKey="completed" position="top" fill="#22C55E" fontSize={10} />
              </Bar>
              <Bar 
                dataKey="partial" 
                stackId="a" 
                fill="#F59E0B" 
                name={translations?.accommodationTasks?.statuses?.partial || "Partial"}
              >
                <LabelList dataKey="partial" position="top" fill="#F59E0B" fontSize={10} />
              </Bar>
              <Bar 
                dataKey="pending" 
                stackId="a" 
                fill="#EF4444" 
                name={translations?.accommodationTasks?.statuses?.pending || "Pending"}
              >
                <LabelList dataKey="pending" position="top" fill="#EF4444" fontSize={10} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Summary stats */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mt-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <h3 className="text-sm font-medium text-gray-500">
              {translations?.accommodationTasks?.statuses?.completed || "Completed"}
            </h3>
            <p className="text-2xl font-bold text-green-600">
              {totals.completed}
              <span className="text-sm font-normal ml-2">
                ({totals.total > 0 ? Math.round((totals.completed / totals.total) * 100) : 0}%)
              </span>
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">
              {translations?.accommodationTasks?.statuses?.partial || "Partial"}
            </h3>
            <p className="text-2xl font-bold text-yellow-600">
              {totals.partial}
              <span className="text-sm font-normal ml-2">
                ({totals.total > 0 ? Math.round((totals.partial / totals.total) * 100) : 0}%)
              </span>
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">
              {translations?.accommodationTasks?.statuses?.pending || "Pending"}
            </h3>
            <p className="text-2xl font-bold text-red-600">
              {totals.pending}
              <span className="text-sm font-normal ml-2">
                ({totals.total > 0 ? Math.round((totals.pending / totals.total) * 100) : 0}%)
              </span>
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
};
